import React, { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Loader2, 
  AlertCircle, 
  Trash2, 
  ChevronLeft, 
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePaginatedProcurements } from "@/hooks/use-paginated-procurements";
import { useDeleteProcurement } from "@/hooks/use-procurement";
import DeleteProcurementDialog from "./DeleteProcurementDialog";

const OptimizedProcurementList: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20); // Fixed page size for now
  const { userRole } = useAuth();
  
  const { data: paginatedData, isLoading, error, refetch } = usePaginatedProcurements(currentPage, pageSize);
  const deleteProcurement = useDeleteProcurement();
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [procurementToDelete, setProcurementToDelete] = useState<string | null>(null);

  const isAdmin = userRole === 'admin';

  const handleDeleteClick = (procurementId: string) => {
    setProcurementToDelete(procurementId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (procurementToDelete) {
      deleteProcurement.mutate(procurementToDelete, {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          setProcurementToDelete(null);
          refetch();
        }
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setProcurementToDelete(null);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Pagination component
  const PaginationControls = () => {
    if (!paginatedData || paginatedData.totalPages <= 1) return null;

    const { currentPage, totalPages } = paginatedData;
    const hasNext = currentPage < totalPages;
    const hasPrev = currentPage > 1;

    return (
      <div className="flex items-center justify-between px-4 py-3 border-t">
        <div className="flex items-center text-sm text-muted-foreground">
          Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, paginatedData.count)} of {paginatedData.count} results
        </div>
        
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(1)}
            disabled={!hasPrev}
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={!hasPrev}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <div className="flex items-center space-x-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(pageNum)}
                  className="w-8 h-8 p-0"
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={!hasNext}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(totalPages)}
            disabled={!hasNext}
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Procurement Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="flex flex-col justify-center items-center py-12">
              <div className="relative">
                <Loader2 className="animate-spin w-12 h-12 text-primary" />
                <div className="absolute inset-0 w-12 h-12 border-2 border-primary/20 rounded-full animate-pulse"></div>
              </div>
              <span className="text-lg font-medium mt-4 text-primary">Loading procurements...</span>
              <span className="text-sm text-muted-foreground mt-1">Please wait while we fetch the latest data</span>
            </div>
            
            {/* Skeleton table structure */}
            <div className="space-y-3">
              <div className="grid grid-cols-7 gap-4 py-3 border-b">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-20" />
              </div>
              {Array.from({ length: 5 }).map((_, index) => (
                <div key={index} className="grid grid-cols-7 gap-4 py-3">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-16" />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Procurement Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center space-y-4 py-6">
            <AlertCircle className="h-12 w-12 text-red-500" />
            <div className="text-red-600 font-medium">Failed to load procurements.</div>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              {error instanceof Error ? error.message : "An unknown error occurred."}
            </p>
            <Button onClick={() => refetch()} variant="outline" size="sm">
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!paginatedData || paginatedData.data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Procurement Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <p className="text-muted-foreground">No procurements found.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex justify-between items-center">
            <span>Procurement Progress</span>
            <span className="text-sm font-normal text-muted-foreground">
              {paginatedData.count} total records
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="px-4">Date</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Quantity</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Requested By</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedData.data.map((p: any) => {
                const createdDate = p.created_at
                  ? new Date(p.created_at).toLocaleDateString()
                  : "–";

                let requestorName = 'System User';
                if (p.requestor) {
                  const firstName = p.requestor.first_name || '';
                  const lastName = p.requestor.last_name || '';
                  const fullName = `${firstName} ${lastName}`.trim();
                  
                  if (fullName) {
                    requestorName = fullName;
                  } else {
                    requestorName = `User ${p.requestor.id.slice(0, 8)}...`;
                  }
                }

                const sourceType = p.source_type || 'Unknown';
                const capitalizedSource = sourceType.charAt(0).toUpperCase() + sourceType.slice(1);
                const quantity = p.total_quantity ? `${p.total_quantity} kg` : "–";
                const productName = p.product_name || '–';

                return (
                  <TableRow key={p.id} className="hover:bg-muted/50">
                    <TableCell className="px-4">{createdDate}</TableCell>
                    <TableCell>{productName}</TableCell>
                    <TableCell>{quantity}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{capitalizedSource}</Badge>
                    </TableCell>
                    <TableCell>{requestorName}</TableCell>
                    <TableCell>
                      <Badge 
                        variant={
                          p.status === 'completed' || p.status === 'confirmed' ? 'default' :
                          p.status === 'pending' ? 'secondary' :
                          p.status === 'rejected' ? 'destructive' :
                          'outline'
                        }
                      >
                        {p.status?.charAt(0).toUpperCase() + p.status?.slice(1).replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/procurements/${p.id}`}>View Details</Link>
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteClick(p.id)}
                            className="text-destructive hover:text-destructive"
                            title="Delete procurement"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          
          <PaginationControls />
        </CardContent>
      </Card>

      <DeleteProcurementDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        isDeleting={deleteProcurement.isPending}
        procurementId={procurementToDelete || ''}
      />
    </>
  );
};

export default OptimizedProcurementList;